import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
    ChevronLeft,
    ChevronRight,
    Pause,
    Play,
    X,
    ExternalLink,
    RotateCcw,
    ArrowRight,
} from "lucide-react";
import { Link } from "react-router-dom";
import {
    isAutoplayableEmbed,
    isDirectVideo,
    toEmbedSrc,
    toReelEmbedSrc,
} from "../lib/embed";

const sourceOpenLabel = (s) =>
    ({
        threads: "Open on Threads",
        instagram: "Open on Instagram",
        vimeo: "Open on Vimeo",
        youtube: "Open on YouTube",
        upload: "Open clip",
    })[s] || "Open clip";

/**
 * Self-curated featured reel.
 * Plays featured clips back-to-back as a cinematic modal player.
 *
 * Props:
 *  - clips: array of featured Clip objects (in order)
 *  - open: boolean — whether modal is shown
 *  - onClose: () => void
 *  - startIndex: number (default 0)
 */
export const FeaturedReelPlayer = ({
    clips = [],
    open = false,
    onClose,
    startIndex = 0,
}) => {
    const [index, setIndex] = useState(startIndex);
    const [playing, setPlaying] = useState(true);
    const [autoAdvance, setAutoAdvance] = useState(() => {
        try {
            return localStorage.getItem("reel_auto") === "1";
        } catch {
            return false;
        }
    });
    // For iframes we force a remount via a key change to apply "pause" (=remove autoplay)
    const [reloadToken, setReloadToken] = useState(0);
    const [reducedMotion, setReducedMotion] = useState(() => {
        if (typeof window === "undefined" || !window.matchMedia) return false;
        return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    });
    // End-of-reel state — shown after the last clip finishes (auto or manual next).
    const [ended, setEnded] = useState(false);
    // Idle = no pointer movement for >2s. Drives chrome + cursor fade.
    const [idle, setIdle] = useState(false);
    const videoRef = useRef(null);
    const modalRef = useRef(null);

    const total = clips.length;
    const clip = total > 0 && !ended ? clips[Math.min(index, total - 1)] : null;

    const next = useCallback(() => {
        setIndex((i) => {
            const nextIdx = i + 1;
            if (nextIdx >= total) {
                setEnded(true);
                setPlaying(false);
                return i;
            }
            return nextIdx;
        });
        if (!ended) {
            setPlaying(true);
            setReloadToken((t) => t + 1);
        }
    }, [total, ended]);

    const prev = useCallback(() => {
        setEnded(false);
        setIndex((i) => (i - 1 + Math.max(total, 1)) % Math.max(total, 1));
        setPlaying(true);
        setReloadToken((t) => t + 1);
    }, [total]);

    const replay = useCallback(() => {
        setEnded(false);
        setIndex(0);
        setPlaying(true);
        setReloadToken((t) => t + 1);
    }, []);

    const togglePlay = useCallback(() => {
        setPlaying((p) => {
            const np = !p;
            // For iframes we re-render with/without autoplay by bumping the reload token.
            if (clip && !isDirectVideo(clip.embed_url)) {
                setReloadToken((t) => t + 1);
            }
            return np;
        });
    }, [clip]);

    // Reset to startIndex whenever modal is freshly opened.
    useEffect(() => {
        if (open) {
            setIndex(startIndex);
            setPlaying(true);
            setReloadToken(0);
            setEnded(false);
            setIdle(false);
        }
    }, [open, startIndex]);

    // Idle detection — fade chrome + cursor after 2s of no pointer movement
    // while a clip is playing. Resets on any move, key, or pause.
    useEffect(() => {
        if (!open) return undefined;
        if (!playing || ended) {
            setIdle(false);
            return undefined;
        }
        let timer = null;
        const arm = () => {
            setIdle(false);
            if (timer) clearTimeout(timer);
            timer = setTimeout(() => setIdle(true), 2000);
        };
        arm();
        const events = ["pointermove", "pointerdown", "keydown", "wheel"];
        events.forEach((e) => window.addEventListener(e, arm, { passive: true }));
        return () => {
            if (timer) clearTimeout(timer);
            events.forEach((e) => window.removeEventListener(e, arm));
        };
    }, [open, playing, ended, index]);

    // HTML5 <video> sync — play/pause when state changes.
    useEffect(() => {
        const el = videoRef.current;
        if (!el) return;
        if (playing) {
            el.play?.().catch(() => {
                /* autoplay blocked — leave paused */
            });
        } else {
            el.pause?.();
        }
    }, [playing, index]);

    // Persist auto-advance preference.
    useEffect(() => {
        try {
            localStorage.setItem("reel_auto", autoAdvance ? "1" : "0");
        } catch {
            /* ignore */
        }
    }, [autoAdvance]);

    // Auto-advance for iframe embeds — HTML5 <video> uses onEnded directly.
    // Gentle default timer (10s); per-clip override via clip.auto_advance_seconds (3-60).
    // Resets on clip change, pause, or toggle off.
    useEffect(() => {
        if (!open || !clip || !autoAdvance || !playing) return undefined;
        if (isDirectVideo(clip.embed_url)) return undefined;
        const rawSecs = Number(clip.auto_advance_seconds);
        const secs =
            Number.isFinite(rawSecs) && rawSecs >= 3 && rawSecs <= 60
                ? rawSecs
                : 10;
        const t = setTimeout(() => next(), secs * 1000);
        return () => clearTimeout(t);
    }, [open, clip, autoAdvance, playing, next, reloadToken]);

    // Keyboard controls.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === " " || e.code === "Space") {
                e.preventDefault();
                togglePlay();
            }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open, onClose, next, prev, togglePlay]);

    // Lock body scroll while open.
    useEffect(() => {
        if (!open) return undefined;
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => {
            document.body.style.overflow = prevOverflow;
        };
    }, [open]);

    // Track prefers-reduced-motion changes live.
    useEffect(() => {
        if (typeof window === "undefined" || !window.matchMedia) return undefined;
        const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
        const onChange = () => setReducedMotion(mq.matches);
        if (mq.addEventListener) mq.addEventListener("change", onChange);
        else if (mq.addListener) mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener("change", onChange);
            else if (mq.removeListener) mq.removeListener(onChange);
        };
    }, []);

    // Effective per-clip duration (clamped 3-60; else 10s default).
    const effectiveSeconds = useMemo(() => {
        const n = Number(clip?.auto_advance_seconds);
        return Number.isFinite(n) && n >= 3 && n <= 60 ? n : 10;
    }, [clip]);

    // Intro overlay — shows briefly when a clip with show_intro_overlay flips in.
    const [showIntro, setShowIntro] = useState(false);
    useEffect(() => {
        if (!open || !clip?.show_intro_overlay) {
            setShowIntro(false);
            return undefined;
        }
        setShowIntro(true);
        const t = setTimeout(() => setShowIntro(false), 3400);
        return () => clearTimeout(t);
    }, [open, clip?.id, clip?.show_intro_overlay]);

    // First-clip viewing hint — fades in during the opening second and out
    // after ~4s so first-time visitors learn the chrome-on-move language.
    // Resets each time the modal is freshly opened (NOT on replay or seeking).
    const [showHint, setShowHint] = useState(false);
    useEffect(() => {
        if (!open) {
            setShowHint(false);
            return undefined;
        }
        setShowHint(true);
        const t = setTimeout(() => setShowHint(false), 4200);
        return () => clearTimeout(t);
    }, [open]);

    const iframeSrc = useMemo(() => {
        if (!clip) return null;
        if (isDirectVideo(clip.embed_url)) return null;
        return playing ? toReelEmbedSrc(clip.embed_url) : toEmbedSrc(clip.embed_url);
    }, [clip, playing]);

    if (!open) return null;
    if (!clip && !ended) return null;

    const canAutoplay = clip
        ? isDirectVideo(clip.embed_url) || isAutoplayableEmbed(clip.source)
        : false;
    const chromeHidden = idle && playing && !ended;

    const modal = (
        <div
            ref={modalRef}
            data-testid="featured-reel-modal"
            data-idle={chromeHidden ? "true" : "false"}
            className={`fixed inset-0 z-[90] bg-black/95 backdrop-blur-xl flex flex-col ${
                chromeHidden ? "reel-cursor-hidden" : ""
            }`}
            role="dialog"
            aria-modal="true"
        >
            {/* Surrounding-UI dim — a slightly heavier wash than the player
                so the chrome (header/controls) feels like the room and the
                player is the screen. */}
            <div
                aria-hidden
                className="absolute inset-0 pointer-events-none bg-black/40"
            />

            {/* Header strip */}
            <div
                className={`relative flex items-center justify-between px-4 sm:px-8 py-4 sm:py-5 border-b border-border/40 reel-chrome ${
                    chromeHidden ? "reel-chrome-hidden-top" : ""
                }`}
            >
                <div className="min-w-0">
                    {!ended && clip && (
                        <>
                            <p className="text-[10px] tracking-[0.3em] uppercase text-muted-foreground">
                                Featured Reel —{" "}
                                {String(index + 1).padStart(2, "0")} /{" "}
                                {String(total).padStart(2, "0")}
                            </p>
                            <p
                                data-testid="featured-reel-title"
                                className="font-serif-display text-lg sm:text-2xl tracking-tight truncate mt-1"
                            >
                                {clip.title || clip.project_name || "Untitled"}
                            </p>
                        </>
                    )}
                </div>
                <button
                    type="button"
                    onClick={onClose}
                    data-testid="featured-reel-close"
                    className="text-label text-foreground/80 hover:text-foreground inline-flex items-center gap-2 shrink-0"
                    aria-label="Close reel"
                >
                    <X size={16} strokeWidth={1.25} />
                    <span className="hidden sm:inline">Close</span>
                </button>
            </div>

            {/* Player */}
            <div className="relative flex-1 flex items-center justify-center px-4 sm:px-8 py-6">
                <div className="w-full max-w-5xl aspect-video bg-black overflow-hidden cinematic-shadow relative">
                    {ended ? (
                        <div
                            data-testid="featured-reel-end-card"
                            className="reel-endcard absolute inset-0 flex flex-col items-center justify-center text-center px-6"
                        >
                            <p className="font-serif-display text-4xl sm:text-6xl tracking-tight leading-tight">
                                End of reel
                            </p>
                            <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
                                <button
                                    type="button"
                                    onClick={replay}
                                    data-testid="featured-reel-replay"
                                    className="inline-flex items-center gap-2 bg-foreground text-background px-5 py-3 text-label hover:bg-foreground/90 transition-colors duration-500"
                                >
                                    <RotateCcw size={14} strokeWidth={1.5} />
                                    Replay
                                </button>
                                <Link
                                    to="/work"
                                    onClick={onClose}
                                    data-testid="featured-reel-view-all"
                                    className="inline-flex items-center gap-2 border border-border hover:border-foreground px-5 py-3 text-label text-foreground/85 hover:text-foreground transition-colors duration-500"
                                >
                                    View All Work
                                    <ArrowRight size={14} strokeWidth={1.25} />
                                </Link>
                            </div>
                        </div>
                    ) : (
                        <>
                            {/* Progress — hidden when chrome is idle/hidden */}
                            {autoAdvance && (reducedMotion ? (
                                <div
                                    data-testid="reel-progress-segments"
                                    className={`absolute top-0 left-0 right-0 z-20 flex gap-1 px-2 pt-1 pointer-events-none reel-chrome ${
                                        chromeHidden ? "reel-chrome-hidden-top" : ""
                                    }`}
                                    aria-hidden
                                >
                                    {clips.map((c, i) => (
                                        <div
                                            key={c.id || `progress-segment-${i}`}
                                            className={`flex-1 h-px ${
                                                i <= index
                                                    ? "bg-foreground/40"
                                                    : "bg-foreground/15"
                                            }`}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <div
                                    data-testid="reel-progress-track"
                                    className={`absolute top-0 left-0 right-0 z-20 h-px bg-foreground/10 overflow-hidden pointer-events-none reel-chrome ${
                                        chromeHidden ? "reel-chrome-hidden-top" : ""
                                    }`}
                                    aria-hidden
                                >
                                    <div
                                        key={`prog-${clip.id}-${reloadToken}`}
                                        data-testid="reel-progress-fill"
                                        className="reel-progress-fill h-full bg-foreground/40 w-full"
                                        style={{
                                            ["--prog-dur"]: `${effectiveSeconds}s`,
                                            animationPlayState: playing
                                                ? "running"
                                                : "paused",
                                        }}
                                    />
                                </div>
                            ))}
                            <div
                                key={`fade-${clip.id}`}
                                className="reel-crossfade absolute inset-0"
                            >
                                {/* Intro overlay — opt-in per clip */}
                                {showIntro && (clip.title || clip.caption) && (
                                    <div
                                        data-testid="reel-intro-overlay"
                                        className="reel-overlay-card absolute bottom-6 left-6 sm:bottom-10 sm:left-10 z-20 max-w-md pointer-events-none"
                                        key={`overlay-${clip.id}`}
                                    >
                                        {clip.title && (
                                            <p className="text-[10px] tracking-[0.32em] uppercase text-foreground/75 mb-3">
                                                {clip.title}
                                            </p>
                                        )}
                                        {clip.caption && (
                                            <p className="font-serif-display text-xl sm:text-2xl tracking-tight leading-snug text-foreground">
                                                {clip.caption}
                                            </p>
                                        )}
                                    </div>
                                )}
                                {isDirectVideo(clip.embed_url) ? (
                                    <video
                                        ref={videoRef}
                                        key={`vid-${clip.id}-${reloadToken}`}
                                        src={clip.embed_url}
                                        controls={!chromeHidden}
                                        playsInline
                                        autoPlay
                                        onEnded={next}
                                        className="w-full h-full object-contain bg-black"
                                        poster={
                                            clip.poster_url ||
                                            clip.thumbnail_url ||
                                            undefined
                                        }
                                    />
                                ) : iframeSrc ? (
                                    <iframe
                                        key={`iframe-${clip.id}-${reloadToken}`}
                                        title={clip.title || "Featured clip"}
                                        src={iframeSrc}
                                        className="w-full h-full"
                                        allow="autoplay; fullscreen; picture-in-picture"
                                        allowFullScreen
                                    />
                                ) : (
                                    <div
                                        data-testid="featured-reel-fallback"
                                        className="relative w-full h-full flex flex-col items-center justify-center text-center p-8 sm:p-12 gap-6"
                                    >
                                        {(clip.poster_url || clip.thumbnail_url) && (
                                            <img
                                                src={
                                                    clip.poster_url ||
                                                    clip.thumbnail_url
                                                }
                                                alt={clip.title || ""}
                                                className="absolute inset-0 w-full h-full object-cover opacity-30"
                                            />
                                        )}
                                        <div className="relative">
                                            <p className="font-serif-display text-2xl sm:text-3xl leading-snug max-w-xl">
                                                This clip plays best on its
                                                source.
                                            </p>
                                            {clip.embed_url && (
                                                <a
                                                    href={clip.embed_url}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                    data-testid="featured-reel-open-source"
                                                    className="mt-6 inline-flex items-center gap-2 text-label underline-anim text-foreground hover:text-foreground"
                                                >
                                                    {sourceOpenLabel(clip.source)}{" "}
                                                    <ExternalLink
                                                        size={14}
                                                        strokeWidth={1.25}
                                                    />
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>

                            {/* Faint grain — sits above the player, below vignette */}
                            <div aria-hidden className="reel-grain" />

                            {/* First-time viewing hint — fades in/out within ~4s */}
                            {showHint && (
                                <div
                                    data-testid="reel-viewing-hint"
                                    aria-hidden
                                    className="reel-hint absolute bottom-5 right-5 z-30 pointer-events-none text-[10px] tracking-[0.28em] uppercase text-foreground/65 bg-background/30 backdrop-blur-sm border border-border/40 px-3 py-1.5"
                                >
                                    Move to show controls · Space to pause
                                </div>
                            )}

                            {/* Vignette — the cinematic edge falloff */}
                            <div
                                aria-hidden
                                data-testid="reel-vignette"
                                className="absolute inset-0 reel-vignette"
                            />

                            {/* External-source fallback notice */}
                            {!canAutoplay && iframeSrc && clip.embed_url && (
                                <a
                                    href={clip.embed_url}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="featured-reel-open-source"
                                    className={`absolute bottom-3 right-3 z-30 inline-flex items-center gap-2 text-[10px] tracking-[0.25em] uppercase bg-background/70 backdrop-blur px-3 py-1.5 border border-border text-foreground/80 hover:text-foreground reel-chrome ${
                                        chromeHidden ? "reel-chrome-hidden" : ""
                                    }`}
                                >
                                    {sourceOpenLabel(clip.source)}{" "}
                                    <ExternalLink size={12} strokeWidth={1.25} />
                                </a>
                            )}
                        </>
                    )}
                </div>
            </div>

            {/* Controls */}
            <div
                className={`relative px-4 sm:px-8 pb-20 sm:pb-8 reel-chrome ${
                    chromeHidden ? "reel-chrome-hidden" : ""
                }`}
            >
                <div className="mx-auto max-w-5xl flex items-center justify-between gap-4">
                    <div className="hidden sm:flex items-center gap-3">
                        {/* Thumbnail strip */}
                        {clips.slice(0, 8).map((c, i) => (
                            <button
                                key={c.id}
                                type="button"
                                onClick={() => {
                                    setEnded(false);
                                    setIndex(i);
                                    setPlaying(true);
                                    setReloadToken((t) => t + 1);
                                }}
                                aria-label={`Go to clip ${i + 1}`}
                                data-testid={`featured-reel-thumb-${i}`}
                                className={`w-12 h-8 overflow-hidden border transition-all duration-500 ${
                                    i === index && !ended
                                        ? "border-foreground opacity-100"
                                        : "border-border opacity-60 hover:opacity-100"
                                }`}
                            >
                                {(c.poster_url || c.thumbnail_url) && (
                                    <img
                                        src={c.poster_url || c.thumbnail_url}
                                        alt=""
                                        className="w-full h-full object-cover"
                                    />
                                )}
                            </button>
                        ))}
                    </div>

                    <div className="flex items-center gap-6 mx-auto sm:mx-0">
                        <button
                            type="button"
                            onClick={prev}
                            data-testid="featured-reel-prev"
                            aria-label="Previous clip"
                            className="p-2 text-foreground/80 hover:text-foreground transition-colors disabled:opacity-30"
                            disabled={total < 2 || ended}
                        >
                            <ChevronLeft size={22} strokeWidth={1.25} />
                        </button>
                        <button
                            type="button"
                            onClick={togglePlay}
                            data-testid="featured-reel-toggle"
                            aria-label={playing ? "Pause" : "Play"}
                            disabled={ended}
                            className="p-3 border border-border hover:border-foreground transition-colors disabled:opacity-30"
                        >
                            {playing ? (
                                <Pause size={18} strokeWidth={1.25} />
                            ) : (
                                <Play
                                    size={18}
                                    strokeWidth={1.25}
                                    className="fill-current"
                                />
                            )}
                        </button>
                        <button
                            type="button"
                            onClick={next}
                            data-testid="featured-reel-next"
                            aria-label="Next clip"
                            className="p-2 text-foreground/80 hover:text-foreground transition-colors disabled:opacity-30"
                            disabled={total < 2 || ended}
                        >
                            <ChevronRight size={22} strokeWidth={1.25} />
                        </button>
                    </div>

                    <div className="hidden sm:flex items-center justify-end min-w-[120px]">
                        <button
                            type="button"
                            onClick={() => setAutoAdvance((v) => !v)}
                            data-testid="featured-reel-auto-toggle"
                            aria-pressed={autoAdvance}
                            aria-label="Toggle auto-advance"
                            className={`inline-flex items-center gap-2 px-3 py-2 border text-[10px] tracking-[0.28em] uppercase transition-colors ${
                                autoAdvance
                                    ? "border-foreground/60 text-foreground"
                                    : "border-border text-foreground/55 hover:text-foreground"
                            }`}
                            title="Auto-advance the reel after each clip"
                        >
                            <span
                                aria-hidden
                                className={`inline-block w-1.5 h-1.5 rounded-full transition-colors ${
                                    autoAdvance
                                        ? "bg-foreground"
                                        : "bg-foreground/30"
                                }`}
                            />
                            Auto · {autoAdvance ? "On" : "Off"}
                        </button>
                    </div>
                </div>

                {/* Mobile auto-advance toggle */}
                <div className="sm:hidden mt-5 flex justify-center">
                    <button
                        type="button"
                        onClick={() => setAutoAdvance((v) => !v)}
                        data-testid="featured-reel-auto-toggle-mobile"
                        aria-pressed={autoAdvance}
                        aria-label="Toggle auto-advance"
                        className={`inline-flex items-center gap-2 px-3 py-2 border text-[10px] tracking-[0.28em] uppercase transition-colors ${
                            autoAdvance
                                ? "border-foreground/60 text-foreground"
                                : "border-border text-foreground/55"
                        }`}
                    >
                        <span
                            aria-hidden
                            className={`inline-block w-1.5 h-1.5 rounded-full ${
                                autoAdvance
                                    ? "bg-foreground"
                                    : "bg-foreground/30"
                            }`}
                        />
                        Auto-advance · {autoAdvance ? "On" : "Off"}
                    </button>
                </div>
            </div>
        </div>
    );

    return createPortal(modal, document.body);
};

export default FeaturedReelPlayer;
